import { useState } from "react";
import { ChevronRight, Target, Compass, TrendingUp, Ruler } from "lucide-react";

/**
 * Het LevelLeven Transitiemodel, 9 fasen.
 * Elke fase heeft: verandering, interventies, doelen, meetbaar.
 */
const fasen = [
  {
    n: "01",
    key: "stilstand",
    naam: "Stilstand",
    kort: "Niets beweegt.",
    verandering:
      "De deelnemer ervaart uitputting en verdoving. Weinig energie, weinig ritme, weinig toekomstgevoel. Trajecten schuiven langs, niets landt.",
    interventies: [
      "Rust en veiligheid creëren",
      "Warme kennismaking, geen intake-formulier",
      "Basisritme: eten, slapen, aanwezig zijn",
    ],
    doelen: ["Voelen dat er iets mogelijk is", "Aanwezig zijn zonder verwachting"],
    meetbaar:
      "Aanwezigheid bij bijeenkomsten, ervaren rust (self-report), fysiek herstel (slaap, energie).",
  },
  {
    n: "02",
    key: "inzicht",
    naam: "Inzicht",
    kort: "Wat is hier eigenlijk aan de hand?",
    verandering:
      "Deelnemer krijgt taal voor wat er speelt. Onderscheid tussen 'lui' en 'opgebrand' wordt zichtbaar. Herkenning brengt lucht.",
    interventies: [
      "NLP-taalwerk en herkaderen",
      "Systemisch bekijken: wat draag ik voor anderen?",
      "Gedeelde groepsherkenning in kleine kring",
    ],
    doelen: ["Erkenning van eigen situatie", "Woorden vinden voor patronen"],
    meetbaar:
      "Zelf-reflectie in groepsgesprekken, verandering in taal (van slachtoffer naar observator).",
  },
  {
    n: "03",
    key: "eigenaarschap",
    naam: "Eigenaarschap",
    kort: "Dit is van mij.",
    verandering:
      "De deelnemer beweegt van 'het overkomt mij' naar 'ik kan iets kiezen'. Klein maar cruciaal.",
    interventies: [
      "Transactionele Analyse, het Volwassene-perspectief oefenen",
      "Reflectie op keuzes en gevolgen",
      "Kleine dagelijkse micro-beslissingen",
    ],
    doelen: ["Eigen doel formuleren", "Regie ervaren op één leefgebied"],
    meetbaar: "Deelnemer formuleert eigen doel, neemt initiatief in de groep.",
  },
  {
    n: "04",
    key: "activering",
    naam: "Activering",
    kort: "Ik doe iets.",
    verandering:
      "De eerste stap buiten de comfortzone. Meestal klein: een wandeling, een boodschap, een gesprek met de buurvrouw.",
    interventies: [
      "Concrete micro-opdrachten tussen bijeenkomsten door",
      "Buddy-koppeling voor accountability",
      "Ankertechnieken voor moeilijke momenten",
    ],
    doelen: ["Wekelijkse actie zonder trainer", "Buddy actief onderhouden"],
    meetbaar: "Aantal actie-vervullingen per week, buddy-contactfrequentie.",
  },
  {
    n: "05",
    key: "structuur",
    naam: "Structuur",
    kort: "Er is een ritme.",
    verandering:
      "Dagen krijgen vorm. Slaap-, eet- en beweegritme stabiliseren. Afspraken worden nagekomen.",
    interventies: [
      "Weekplanning met concrete blokken",
      "Gezondheidscoaching (voeding, slaap, beweging)",
      "Vaste tweewekelijkse bijeenkomsten",
    ],
    doelen: ["Consistente dagstructuur", "Verminderd stress-niveau"],
    meetbaar: "Aanwezigheidsscore, zelfrapportage-schaal dagstructuur (1-10).",
  },
  {
    n: "06",
    key: "belastbaarheid",
    naam: "Belastbaarheid",
    kort: "Ik kan meer aan.",
    verandering:
      "Fysieke en mentale belastbaarheid groeien. Setbacks veroorzaken geen totale terugval meer.",
    interventies: [
      "Toenemende oefenintensiteit in blokken 3 en 4",
      "Opstellingen rond stress en grenzen",
      "Werken aan zelfregie in moeilijke situaties",
    ],
    doelen: ["Volhouden bij tegenslag", "20+ uur per week actief"],
    meetbaar: "Uren-op-been per week, hersteltijd na tegenvaller.",
  },
  {
    n: "07",
    key: "perspectief",
    naam: "Toekomstperspectief",
    kort: "Ik zie waar ik naartoe wil.",
    verandering:
      "De deelnemer ziet zichzelf ergens naartoe bewegen. Niet als abstract doel maar als concrete richting.",
    interventies: [
      "Levensscript-werk en waarden",
      "Kennismaking met werkgevers, opleidingen, vrijwilligerswerk",
      "Ontwikkelplan met de deelnemer als eigenaar",
    ],
    doelen: ["Gekozen richting", "Volgende-stap plan"],
    meetbaar: "Deelnemer benoemt eigen vervolgstap; datum voor eerste actie is gepland.",
  },
  {
    n: "08",
    key: "participatie",
    naam: "Participatie",
    kort: "Ik doe mee.",
    verandering:
      "Actieve maatschappelijke deelname: vrijwilligerswerk, mantelzorg, wijkactiviteit of stage. Verbinding wordt duurzaam.",
    interventies: [
      "Warme match met wijkorganisatie of werkgever",
      "Certificering van doorlopen ontwikkeling",
      "Voortzetting van buddy-relatie",
    ],
    doelen: ["Wekelijkse maatschappelijke inzet", "Uitstroomplan concreet"],
    meetbaar: "Aantal uren participatie per week, tevredenheid van host-organisatie.",
  },
  {
    n: "09",
    key: "werk",
    naam: "Werk",
    kort: "Passende plaatsing.",
    verandering:
      "Uitstroom naar loondienst, opleiding, ondernemerschap of duurzame vrijwillige inzet, passend bij talent én belastbaarheid.",
    interventies: [
      "Overdracht naar reguliere re-integratie of jobcoach",
      "Werkgevers-training voor de nieuwe werkomgeving",
      "Nazorg via buddy-netwerk",
    ],
    doelen: ["Duurzame plaatsing", "Terugvalpreventie"],
    meetbaar: "Behoud van plaatsing na 6 en 12 maanden, terugval-signalen.",
  },
];

export default function Transitiemodel() {
  const [active, setActive] = useState("stilstand");
  const cur = fasen.find((f) => f.key === active);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
      {/* Rail, fasen als klikbare knoppen */}
      <div className="lg:col-span-5">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">
          Het LevelLeven Transitiemodel
        </p>
        <h3 className="font-heading text-3xl text-[#1D2A22] leading-tight font-light mb-8">
          Negen fasen.{" "}
          <span className="italic text-[#E06B3F]">Eén logische volgorde.</span>
        </h3>
        <ol className="space-y-1.5">
          {fasen.map((f, i) => {
            const isActive = active === f.key;
            return (
              <li key={f.key}>
                <button
                  type="button"
                  data-testid={`fase-${f.key}`}
                  onClick={() => setActive(f.key)}
                  aria-current={isActive}
                  className={`w-full text-left flex items-baseline gap-4 rounded-2xl px-4 py-3 transition-colors ${
                    isActive
                      ? "bg-[#1D2A22] text-[#FAF7F1]"
                      : "hover:bg-[#EFECE5] text-[#1D2A22]"
                  }`}
                >
                  <span
                    className={`text-xs uppercase tracking-[0.2em] shrink-0 w-8 ${
                      isActive ? "text-[#D19B72]" : "text-[#E06B3F]"
                    }`}
                  >
                    {f.n}
                  </span>
                  <span className="flex-1">
                    <span className="font-heading text-2xl leading-none block">
                      {f.naam}
                    </span>
                    <span
                      className={`text-xs mt-1 block ${
                        isActive ? "text-[#FAF7F1]/60" : "text-[#7A8276]"
                      }`}
                    >
                      {f.kort}
                    </span>
                  </span>
                  <ChevronRight
                    size={16}
                    className={`shrink-0 transition-transform ${
                      isActive ? "translate-x-1" : "opacity-40"
                    }`}
                  />
                </button>
                {i < fasen.length - 1 && (
                  <div className="ml-8 h-2 w-px bg-[#E5E1D8]" />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Detail */}
      <div className="lg:col-span-7">
        <div
          key={cur.key}
          data-testid={`fase-detail-${cur.key}`}
          className="rounded-3xl border border-[#E5E1D8] bg-[#EFECE5] p-8 sm:p-10 animate-in fade-in slide-in-from-bottom-4 duration-500"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-3">
            Fase {cur.n} · {cur.naam}
          </p>
          <h4 className="font-heading text-3xl sm:text-4xl text-[#1D2A22] leading-tight font-light">
            {cur.kort}
          </h4>
          <p className="mt-5 text-[#4A5D4E] leading-relaxed font-light">
            {cur.verandering}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Block icon={Compass} titel="Interventies">
              <ul className="space-y-1.5 mt-2">
                {cur.interventies.map((x, i) => (
                  <li key={i} className="text-sm text-[#1D2A22]">
                    · {x}
                  </li>
                ))}
              </ul>
            </Block>
            <Block icon={Target} titel="Doelen">
              <ul className="space-y-1.5 mt-2">
                {cur.doelen.map((x, i) => (
                  <li key={i} className="text-sm text-[#1D2A22]">
                    · {x}
                  </li>
                ))}
              </ul>
            </Block>
            <Block icon={TrendingUp} titel="Wat verandert er">
              <p className="mt-2 text-sm text-[#4A5D4E]">
                {cur.verandering.split(".")[0]}.
              </p>
            </Block>
            <Block icon={Ruler} titel="Meetbaar">
              <p className="mt-2 text-sm text-[#4A5D4E]">{cur.meetbaar}</p>
            </Block>
          </div>
        </div>

        {/* Waarom niet bij werk beginnen */}
        <div className="mt-4 rounded-2xl bg-[#1D2A22] text-[#FAF7F1] p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D19B72] mb-2">
            Waarom niet bij "Werk" beginnen?
          </p>
          <p className="text-sm leading-relaxed text-[#FAF7F1]/85">
            Werk is fase 9, niet fase 1. Beginnen bij werk zonder fundament
            (fase 1-6) leidt tot uitval, terugval en herhaalde intake-carrousels.
            Het Transitiemodel bouwt eerst wat re-integratie veronderstelt.
          </p>
        </div>
      </div>
    </div>
  );
}

function Block({ icon: Icon, titel, children }) {
  return (
    <div className="rounded-2xl bg-[#FAF7F1] border border-[#E5E1D8] p-5">
      <div className="flex items-center gap-2 text-[#E06B3F]">
        <Icon size={14} />
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold">
          {titel}
        </p>
      </div>
      {children}
    </div>
  );
}
